import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { FiStar, FiBarChart2, FiGrid, FiList, FiRefreshCw } from "react-icons/fi";
import { AiOutlineClose } from "react-icons/ai";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink, useNavigate } from "react-router-dom";
import { masterSchemeList } from "../../api/bseApi";

const MAX_COMPARE = 4;
const PAGE_SIZE = 20;

const CATEGORY_OPTIONS = [
  "Equity",
  "Debt",
  "Hybrid",
  "Solution Oriented",
  "Other",
  "ELSS",
];

const PLAN_OPTIONS = ["Direct", "Regular"];

function mapBseSchemeToFund(scheme) {
  const id =
    scheme.scheme_isin ||
    scheme.scheme_bse_code ||
    scheme.scheme_amfi_code ||
    scheme.name;
  return {
    id,
    isin: scheme.scheme_isin || "",
    bseCode: scheme.scheme_bse_code || "",
    name: scheme.name || scheme.parent_scheme_name || "Unknown Scheme",
    parentName: scheme.parent_scheme_name || "",
    amc: scheme.scheme_amc_name || "N/A",
    category: scheme.scheme_category || "N/A",
    subCategory: scheme.scheme_sub_category || "",
    plan: scheme.scheme_plan || "N/A",
    option: scheme.scheme_option || "",
    isActive: !!scheme.is_active,
    sipAllowed: !!scheme.sip_flag,
    riskBadge: scheme.scheme_sub_category || scheme.scheme_category || "N/A",
    rawScheme: scheme,
  };
}

export default function Explore() {
  const [funds, setFunds] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    scheme_amc_name: "",
    scheme_category: "",
    scheme_plan: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [compareList, setCompareList] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [showCompareTable, setShowCompareTable] = useState(false);
  const [viewMode, setViewMode] = useState("grid");

  const compareDrawerRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  const fetchSchemes = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const payload = {
        data: {
          fields: ["ALL"],
          count_only: false,
          start: (currentPage - 1) * PAGE_SIZE,
          length: PAGE_SIZE,
          search: { value: search.trim() },
          filter_param: {
            scheme_amc_name: filters.scheme_amc_name || "",
            scheme_category: filters.scheme_category || "",
            scheme_sub_category: "",
            scheme_option: "",
            scheme_plan: filters.scheme_plan || "",
            scheme_isin: "",
            settlement_days: "",
          },
        },
      };

      const res = await masterSchemeList(payload);
      const body = res?.data || {};
      const lists = Array.isArray(body?.data?.lists) ? body.data.lists : [];
      const count = Number(body?.data?.count ?? lists.length) || lists.length;

      setFunds(lists.map(mapBseSchemeToFund));
      setTotalCount(count);
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.data?.messages?.[0]?.errcode ||
        err?.message ||
        "Failed to fetch schemes";
      setErrorMsg(String(apiMsg));
      setFunds([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, filters]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSchemes();
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchSchemes]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filters.scheme_amc_name, filters.scheme_category, filters.scheme_plan]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)),
    [totalCount]
  );
  const noDataFound = !loading && funds.length === 0;

  const compareFunds = compareList
    .map((id) => funds.find((f) => f.id === id))
    .filter(Boolean);

  const toggleWatchlist = (id) => {
    setWatchlist((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );
  };

  const toggleCompare = (id) => {
    setCompareList((prev) => {
      if (prev.includes(id)) return prev.filter((f) => f !== id);
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, id];
    });
  };

  const removeFromCompare = (id) => {
    setCompareList((prev) => prev.filter((f) => f !== id));
  };

  const closeCompareDrawer = useCallback(() => {
    setCompareList([]);
    setShowCompareTable(false);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && compareList.length > 0) {
        closeCompareDrawer();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [compareList.length, closeCompareDrawer]);

  useEffect(() => {
    if (compareList.length > 0) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [compareList.length]);

  const isCompareDisabled = compareList.length >= MAX_COMPARE;

  const handleBuy = (fund) => {
    navigate("/lumpsuminvest", { state: { selectedFund: fund } });
  };

  const handleSell = (fund) => {
    navigate("/redeemMf", { state: { selectedFund: fund } });
  };

  return (
    <>
      <ClientNavbar />
      <div className="page-content" style={{ background: "#f1f5f9" }}>
        <div className="card shadow-sm p-4 mb-4">
          <h3 className="text-primary mb-2">Explore Mutual Funds</h3>
          <nav
            aria-label="breadcrumb"
            style={{ "--bs-breadcrumb-divider": "none" }}
          >
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <NavLink to="/overview">Dashboard</NavLink> /
              </li>
              <li className="breadcrumb-item">
                <NavLink to="/clientmutualfunds">Mutual Funds</NavLink> /
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Explore Mutual Funds
              </li>
            </ol>
          </nav>
        </div>

        <div className="card shadow-sm p-4 mb-4">
          {compareList.length > 0 && (
            <div
              style={{
                padding: "10px 16px",
                marginBottom: 16,
                background: "linear-gradient(135deg, #ccfbf1 0%, #99f6e4 100%)",
                borderRadius: 10,
                border: "1px solid #5eead4",
                fontSize: 14,
                color: "#0f766e",
                fontWeight: 600,
              }}
            >
              {compareList.length} fund{compareList.length !== 1 ? "s" : ""} selected — Click more fund cards to add (max {MAX_COMPARE})
            </div>
          )}

          <div className="explore-controls" style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            <input
              type="text"
              placeholder="Search by scheme name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="explore-search"
              style={{ flex: "1 1 240px", minWidth: 220, padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 8 }}
            />

            <input
              type="text"
              placeholder="AMC name (e.g. Aditya Birla)"
              value={filters.scheme_amc_name}
              onChange={(e) => setFilters({ ...filters, scheme_amc_name: e.target.value })}
              style={{ flex: "1 1 200px", minWidth: 200, padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 8 }}
            />

            <select
              value={filters.scheme_category}
              onChange={(e) => setFilters({ ...filters, scheme_category: e.target.value })}
              style={{ padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 8 }}
            >
              <option value="">All Categories</option>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <select
              value={filters.scheme_plan}
              onChange={(e) => setFilters({ ...filters, scheme_plan: e.target.value })}
              style={{ padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 8 }}
            >
              <option value="">All Plans</option>
              {PLAN_OPTIONS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>

            <button
              type="button"
              onClick={fetchSchemes}
              title="Refresh"
              style={{
                padding: "10px 14px",
                border: "1px solid #0d9488",
                background: "#0d9488",
                color: "#fff",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FiRefreshCw size={16} /> Refresh
            </button>

            <div className="d-flex align-items-center gap-1" style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                title="Grid view"
                style={{
                  padding: "10px 14px",
                  border: "none",
                  background: viewMode === "grid" ? "#0d9488" : "#fff",
                  color: viewMode === "grid" ? "#fff" : "#64748b",
                  cursor: "pointer",
                }}
              >
                <FiGrid size={18} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                title="List view"
                style={{
                  padding: "10px 14px",
                  border: "none",
                  borderLeft: "1px solid #e2e8f0",
                  background: viewMode === "list" ? "#0d9488" : "#fff",
                  color: viewMode === "list" ? "#fff" : "#64748b",
                  cursor: "pointer",
                }}
              >
                <FiList size={18} />
              </button>
            </div>
          </div>

          <div style={{ marginTop: 12, color: "#64748b", fontSize: 13 }}>
            {loading ? "Loading schemes..." : `Showing ${funds.length} of ${totalCount} schemes`}
            {errorMsg && (
              <span style={{ color: "#dc2626", marginLeft: 8 }}>
                Error: {errorMsg}
              </span>
            )}
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
              <h5>Loading schemes from BSE...</h5>
            </div>
          ) : noDataFound ? (
            <div className="no-data-card" style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
              <h5>No schemes match your filters</h5>
              <p style={{ marginTop: 8, fontSize: 13 }}>Try removing some filters or changing search.</p>
            </div>
          ) : viewMode === "list" ? (
            <div className="table-responsive" style={{ marginTop: 12 }}>
              <table className="table table-hover mb-0" style={{ fontSize: 14 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                    <th style={{ padding: "12px 16px", fontWeight: 600, color: "#475569" }}>Scheme</th>
                    <th style={{ padding: "12px 16px", fontWeight: 600, color: "#475569" }}>AMC</th>
                    <th style={{ padding: "12px 16px", fontWeight: 600, color: "#475569" }}>Category</th>
                    <th style={{ padding: "12px 16px", fontWeight: 600, color: "#475569" }}>Plan</th>
                    <th style={{ padding: "12px 16px", fontWeight: 600, color: "#475569", width: 120 }}>Actions</th>
                    <th style={{ padding: "12px 16px", fontWeight: 600, color: "#475569", width: 160 }}>Buy / Sell</th>
                  </tr>
                </thead>
                <tbody>
                  {funds.map((fund) => (
                    <tr key={fund.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "14px 16px", fontWeight: 600, color: "#0f172a" }}>
                        <div>{fund.name}</div>
                        {fund.isin && (
                          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                            ISIN: {fund.isin}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "14px 16px", color: "#64748b" }}>{fund.amc}</td>
                      <td style={{ padding: "14px 16px", color: "#64748b" }}>
                        {fund.category}{fund.subCategory ? ` — ${fund.subCategory}` : ""}
                      </td>
                      <td style={{ padding: "14px 16px", color: "#64748b" }}>{fund.plan}</td>
                      <td style={{ padding: "14px 16px" }}>
                        <button
                          type="button"
                          onClick={() => toggleWatchlist(fund.id)}
                          title={watchlist.includes(fund.id) ? "Remove from watchlist" : "Add to watchlist"}
                          style={{ background: "none", border: "none", padding: 4, cursor: "pointer", marginRight: 8 }}
                        >
                          <FiStar color={watchlist.includes(fund.id) ? "gold" : "gray"} size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleCompare(fund.id)}
                          disabled={isCompareDisabled && !compareList.includes(fund.id)}
                          title={compareList.includes(fund.id) ? "Remove from compare" : "Add to compare"}
                          style={{ background: "none", border: "none", padding: 4, cursor: "pointer" }}
                        >
                          <FiBarChart2 color={compareList.includes(fund.id) ? "#0d9488" : "gray"} size={18} />
                        </button>
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <button
                          type="button"
                          onClick={() => handleBuy(fund)}
                          style={{
                            padding: "6px 12px",
                            marginRight: 8,
                            background: "#10b981",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            fontWeight: 600,
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          Buy
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSell(fund)}
                          style={{
                            padding: "6px 12px",
                            background: "#fff",
                            color: "#ef4444",
                            border: "1px solid #ef4444",
                            borderRadius: 6,
                            fontWeight: 600,
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          Sell
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              className="funds-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 20,
                marginTop: 12,
              }}
            >
              {funds.map((fund) => (
                <div
                  key={fund.id}
                  className="fund-card"
                  style={{
                    padding: 20,
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 12,
                    position: "relative",
                    transition: "box-shadow 0.2s, border-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.08)";
                    e.currentTarget.style.borderColor = "#0d9488";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = "#e2e8f0";
                  }}
                >
                  <h3 className="category-title" style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 8, lineHeight: 1.3 }}>
                    {fund.name}
                  </h3>
                  <p style={{ marginBottom: 4, fontSize: 12, color: "#64748b" }}>
                    <strong>AMC:</strong> {fund.amc}
                  </p>
                  <p style={{ marginBottom: 4, fontSize: 12, color: "#64748b" }}>
                    <strong>Category:</strong> {fund.category}{fund.subCategory ? ` — ${fund.subCategory}` : ""}
                  </p>
                  <p style={{ marginBottom: 14, fontSize: 12, color: "#64748b" }}>
                    <strong>Plan:</strong> {fund.plan}
                  </p>
                  <div className="d-flex flex-wrap gap-2 mb-3" style={{ marginBottom: 12 }}>
                    <button
                      type="button"
                      onClick={() => handleBuy(fund)}
                      style={{
                        padding: "8px 16px",
                        background: "#10b981",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: "pointer",
                        flex: 1,
                        minWidth: 70,
                      }}
                    >
                      Buy
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSell(fund)}
                      style={{
                        padding: "8px 16px",
                        background: "#fff",
                        color: "#ef4444",
                        border: "1px solid #ef4444",
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: "pointer",
                        flex: 1,
                        minWidth: 70,
                      }}
                    >
                      Sell
                    </button>
                  </div>
                  <div className="fund-actions" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      onClick={() => toggleWatchlist(fund.id)}
                      title={watchlist.includes(fund.id) ? "Remove from watchlist" : "Add to watchlist"}
                      style={{ background: "none", border: "none", padding: 6, cursor: "pointer" }}
                    >
                      <FiStar color={watchlist.includes(fund.id) ? "gold" : "gray"} size={20} />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleCompare(fund.id)}
                      disabled={isCompareDisabled && !compareList.includes(fund.id)}
                      title={compareList.includes(fund.id) ? "Remove from compare" : "Add to compare"}
                      style={{ background: "none", border: "none", padding: 6, cursor: "pointer" }}
                    >
                      <FiBarChart2 color={compareList.includes(fund.id) ? "#0d9488" : "gray"} size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && !noDataFound && totalPages > 1 && (
            <div className="pagination-wrapper" style={{ marginTop: 20, display: "flex", justifyContent: "center", gap: 12, alignItems: "center" }}>
              <button
                className="page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                style={{ padding: "8px 14px", border: "1px solid #cbd5e1", background: "#fff", borderRadius: 6, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
              >
                Prev
              </button>
              <span className="page-info">
                Page <strong>{currentPage}</strong> of {totalPages}
              </span>
              <button
                className="page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                style={{ padding: "8px 14px", border: "1px solid #cbd5e1", background: "#fff", borderRadius: 6, cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {compareList.length > 0 && (
          <div
            className="compare-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="compare-title"
            style={{
              position: "fixed",
              inset: 0,
              background: "transparent",
              zIndex: 9999,
              display: "flex",
              justifyContent: "flex-end",
              pointerEvents: "none",
              animation: "fadeIn 0.2s ease",
            }}
          >
            <style>{`
              @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
              @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
            `}</style>
            <div
              ref={compareDrawerRef}
              className="compare-drawer"
              style={{
                position: "relative",
                width: "min(100%, 400px)",
                maxWidth: 400,
                height: "100vh",
                background: "#fff",
                boxShadow: "-8px 0 32px rgba(0,0,0,0.12)",
                display: "flex",
                flexDirection: "column",
                animation: "slideInRight 0.3s ease",
                pointerEvents: "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "18px 20px",
                  borderBottom: "1px solid #e2e8f0",
                  background: "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)",
                  color: "#fff",
                }}
              >
                <h5 id="compare-title" style={{ margin: 0, fontWeight: 700, fontSize: "1.1rem" }}>
                  Compare Funds
                </h5>
                <button
                  onClick={closeCompareDrawer}
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    border: "none",
                    borderRadius: 8,
                    width: 36,
                    height: 36,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#fff",
                    fontSize: 20,
                  }}
                >
                  <AiOutlineClose />
                </button>
              </div>

              <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
                {!showCompareTable ? (
                  <>
                    <p style={{ fontSize: 13, color: "#64748b", marginBottom: 14 }}>
                      {compareList.length} of {MAX_COMPARE} funds selected
                    </p>
                    {compareList.map((id) => {
                      const fund = funds.find((f) => f.id === id);
                      if (!fund) return null;
                      return (
                        <div
                          key={id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            padding: "12px 14px",
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            borderRadius: 10,
                            marginBottom: 10,
                          }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                            {fund.name}
                          </span>
                          <button
                            onClick={() => removeFromCompare(id)}
                            style={{
                              background: "transparent",
                              border: "none",
                              width: 28,
                              height: 28,
                              borderRadius: 6,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              color: "#94a3b8",
                              flexShrink: 0,
                            }}
                          >
                            <AiOutlineClose size={16} />
                          </button>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "10px 8px", borderBottom: "2px solid #0d9488", color: "#0f172a" }}>Fund</th>
                          <th style={{ textAlign: "left", padding: "10px 8px", borderBottom: "2px solid #0d9488", color: "#0f172a" }}>AMC</th>
                          <th style={{ textAlign: "left", padding: "10px 8px", borderBottom: "2px solid #0d9488", color: "#0f172a" }}>Category</th>
                          <th style={{ textAlign: "left", padding: "10px 8px", borderBottom: "2px solid #0d9488", color: "#0f172a" }}>Plan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compareFunds.map((fund) => (
                          <tr key={fund.id}>
                            <td style={{ padding: "10px 8px", borderBottom: "1px solid #e2e8f0", fontWeight: 600 }}>{fund.name}</td>
                            <td style={{ padding: "10px 8px", borderBottom: "1px solid #e2e8f0" }}>{fund.amc}</td>
                            <td style={{ padding: "10px 8px", borderBottom: "1px solid #e2e8f0" }}>{fund.category}</td>
                            <td style={{ padding: "10px 8px", borderBottom: "1px solid #e2e8f0" }}>{fund.plan}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={{ padding: 16, borderTop: "1px solid #e2e8f0", background: "#f8fafc" }}>
                {!showCompareTable ? (
                  <button
                    onClick={() => setShowCompareTable(true)}
                    disabled={compareList.length < 2}
                    style={{
                      width: "100%",
                      padding: "12px 20px",
                      background: compareList.length >= 2 ? "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)" : "#cbd5e1",
                      color: "#fff",
                      border: "none",
                      borderRadius: 10,
                      fontWeight: 600,
                      fontSize: 15,
                      cursor: compareList.length >= 2 ? "pointer" : "not-allowed",
                    }}
                  >
                    Compare Now
                  </button>
                ) : (
                  <button
                    onClick={() => setShowCompareTable(false)}
                    style={{
                      width: "100%",
                      padding: "12px 20px",
                      background: "#fff",
                      color: "#0d9488",
                      border: "2px solid #0d9488",
                      borderRadius: 10,
                      fontWeight: 600,
                      fontSize: 15,
                      cursor: "pointer",
                    }}
                  >
                    Back to List
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
